import {
  CandidateOutcome, CandidateStage, Insights, InsightPerson,
} from '../../api/services/hiring.service';
import { SOURCE_LABEL, STAGE_LABEL } from './hiringUI';

// What the numbers say, as a short ranked list.
//
// Each finding is a rule over the numbers the server returned — nothing
// here is estimated or invented (BRAND.md § 3 rule 4) — and each says
// what happened, then what to do (rule 1). A rule only speaks when its
// numbers are big enough to mean something: "100% of losses" over one
// person is a count wearing a percentage.

export type FindingTone = 'warn' | 'info' | 'ok';

export interface Finding {
  key: string;
  tone: FindingTone;
  title: string;
  action: string;
  people?: InsightPerson[];
  total?: number;
  detail?: (p: InsightPerson) => string | null;
}

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;
const pct = (part: number, whole: number) => (whole ? Math.round((part / whole) * 100) : 0);
const stageName = (s: CandidateStage | string | null) => (s && STAGE_LABEL[s as CandidateStage]) || 'an unknown stage';

/** How each way of losing somebody reads in a sentence, and what fixes it. */
const LOSS: Partial<Record<CandidateOutcome, { verb: string; action: string }>> = {
  unresponsive: { verb: 'went quiet', action: 'Reach them sooner, and follow up before the silence rule closes them.' },
  declined: { verb: 'were declined', action: 'Screen for it earlier, before it costs a stage.' },
  withdrew: { verb: 'withdrew', action: 'Ask the next few what changed their mind here.' },
  no_show: { verb: 'did not show', action: 'Confirm the day before, by text.' },
};

const OWED_ACTION = {
  them: 'It is theirs to send. A reminder text moves it.',
  us: 'It is yours to do. Clear them in one sitting.',
  third_party: 'It is with an outside party. Chase the vendor.',
} as const;

/**
 * @param period how the window reads in a sentence, e.g. "the 90 days before"
 */
export function findings(data: Insights, period: string | null): Finding[] {
  const out: Finding[] = [];
  const open = data.openTotal;

  // Somebody waiting past their stage's limit. First because it is the
  // one that gets worse every day nobody acts.
  if (data.stalledTotal > 0) {
    const top = data.stalled[0];
    out.push({
      key: 'stalled',
      tone: 'warn',
      title: `${plural(data.stalledTotal, 'person has', 'people have')} gone quiet past their stage's limit`
        + (top ? `. ${top.name} has waited ${top.idleDays} days.` : '.'),
      action: 'Message them today, or close them out.',
      people: data.stalled,
      total: data.stalledTotal,
      detail: (p) => (p.idleDays != null ? `${p.idleDays}d` : null),
    });
  }

  // The one step most of the open pipeline is waiting on.
  const block = data.blockers[0];
  if (block && open && (block.count >= 3 || block.count / open >= 0.2)) {
    const owed = (Object.entries(block.owedBy) as [keyof typeof OWED_ACTION, number][])
      .sort((a, b) => b[1] - a[1])[0][0];
    out.push({
      key: 'blocker',
      tone: block.overdue ? 'warn' : 'info',
      title: `${block.count} of ${open} open candidates are waiting on ${block.label.toLowerCase()}`
        + (block.overdue ? `, ${block.overdue} past due` : ''),
      action: OWED_ACTION[owed],
      people: block.people,
      total: block.count,
      detail: (p) => (p.overdueDays ? `${p.overdueDays}d late` : null),
    });
  }

  if (data.readyNoStartTotal > 0) {
    out.push({
      key: 'ready',
      tone: 'warn',
      title: `${plural(data.readyNoStartTotal, 'person is', 'people are')} ready to start with no start date`,
      action: 'Everything else is done. Give them a day.',
      people: data.readyNoStart,
      total: data.readyNoStartTotal,
      detail: (p) => (p.idleDays != null ? `${p.idleDays}d` : null),
    });
  }

  if (data.hiredCount === 0 && data.entered >= 10) {
    out.push({
      key: 'no-hires',
      tone: 'warn',
      title: `No hires yet from the ${data.entered} people added in this window`,
      action: 'Start with the stage where most of them stopped, below.',
    });
  }

  // The single biggest way people are lost: one stage, one ending.
  let leak: { stage: CandidateStage; outcome: CandidateOutcome; n: number } | null = null;
  for (const [stage, byOutcome] of Object.entries(data.lostBy)) {
    for (const [outcome, n] of Object.entries(byOutcome || {})) {
      if (!leak || (n || 0) > leak.n) leak = { stage: stage as CandidateStage, outcome: outcome as CandidateOutcome, n: n || 0 };
    }
  }
  if (leak && data.lostCount >= 5 && leak.n / data.lostCount >= 0.25 && LOSS[leak.outcome]) {
    out.push({
      key: 'leak',
      tone: 'info',
      title: `${pct(leak.n, data.lostCount)}% of the people you lost ${LOSS[leak.outcome]!.verb} in ${stageName(leak.stage)}`
        + ` (${leak.n} of ${data.lostCount})`,
      action: LOSS[leak.outcome]!.action,
    });
  }

  // The weakest step of the funnel, when it is not the leak said again.
  let weakest: { from: CandidateStage; to: CandidateStage; rate: number } | null = null;
  for (let i = 1; i < data.funnel.length; i += 1) {
    const before = data.funnel[i - 1];
    if (before.count < 5) continue;
    const rate = pct(data.funnel[i].count, before.count);
    if (!weakest || rate < weakest.rate) weakest = { from: before.stage, to: data.funnel[i].stage, rate };
  }
  if (weakest && weakest.rate < 60 && weakest.from !== leak?.stage) {
    out.push({
      key: 'funnel',
      tone: 'info',
      title: `Only ${weakest.rate}% of people who reach ${stageName(weakest.from)} get to ${stageName(weakest.to)}`,
      action: 'That step loses the most. Fix it first.',
    });
  }

  // Sources, once at least two have finished enough people to compare.
  const comparable = data.sources
    .filter((s) => s.hired + s.lost >= 5 && s.conversion != null)
    .sort((a, b) => (b.conversion as number) - (a.conversion as number));
  if (comparable.length >= 2 && (comparable[0].conversion as number) > 0) {
    const best = comparable[0];
    const worst = comparable[comparable.length - 1];
    const name = (s: string) => SOURCE_LABEL[s as keyof typeof SOURCE_LABEL] || s;
    const times = worst.conversion ? Math.round(((best.conversion as number) / worst.conversion) * 10) / 10 : null;
    out.push({
      key: 'sources',
      tone: 'ok',
      title: times && times >= 1.5
        ? `${name(best.source)} hires ${times}× as often as ${name(worst.source)} (${best.conversion}% vs ${worst.conversion}%)`
        : `${name(best.source)} hires best, at ${best.conversion}% of the ones that finished`,
      action: `Put more of your outreach into ${name(best.source)}.`,
    });
  }

  // Against the same length of time before, when there was one.
  if (period && data.previous.entered > 0) {
    const was = data.previous.hiredCount;
    const now = data.hiredCount;
    if (was > 0 && now !== was) {
      out.push({
        key: 'pace',
        tone: now > was ? 'ok' : 'warn',
        title: `${plural(now, 'hire', 'hires')} in this window, ${now > was ? 'up' : 'down'} from ${was} ${period}`,
        action: now > was ? 'Whatever changed is working. Keep it.' : 'Check whether fewer people are coming in, or fewer getting through.',
      });
    } else {
      const change = pct(data.entered - data.previous.entered, data.previous.entered);
      if (Math.abs(change) >= 30) {
        out.push({
          key: 'pace',
          tone: change > 0 ? 'ok' : 'warn',
          title: `${data.entered} added, ${change > 0 ? 'up' : 'down'} from ${data.previous.entered} ${period}`,
          action: change > 0 ? 'More coming in. Make sure the first call keeps up.' : 'Fewer coming in. Post again, or ask your drivers for referrals.',
        });
      }
    }
  }

  if (open >= 5 && data.unowned / open >= 0.3) {
    out.push({
      key: 'unowned',
      tone: 'info',
      title: `${data.unowned} of ${open} open candidates have no owner`,
      action: 'Assign them, so every follow-up lands on somebody.',
    });
  }

  return out.slice(0, 6);
}
